import { useEffect, useRef, useState } from 'react';

import type { RecorderProps } from '@karehero/llama/pwa/pages/ConversationalFormPage/ChatComposer';
import { joinLines } from '@karehero/helpers/string';

import { heardText } from 'utils/voiceTranscript';
import {
  MAX_RECORDING_DURATION_SECONDS,
  useAudioRecorder,
} from './useAudioRecorder';
import { useVoiceTranscript } from './useVoiceTranscript';

/** How long they stop talking while recording before what they said is sent. */
export const SPEECH_PAUSE_MS = 1200;

/**
 * Takes a finished voice note: its audio, length, levels, transcript if one is
 * worth reading, and where the API keeps its recording if it holds all of it.
 */
export type VoiceNoteDelivery = (
  file: File,
  durationSeconds: number,
  levels: readonly number[],
  transcript: string | null,
  recordingId: string | null,
) => void;

/**
 * Sends a voice note's words in the flow of a chat: those said up to each
 * pause while it is recorded, and the recording once it stops.
 */
export interface VoiceNoteFlow {
  /** Sends the words said since the last pause, giving back the message they went in. */
  sendWords: (
    words: string,
    durationSeconds: number,
    levels: readonly number[],
  ) => string;
  /** Keeps the whole recording with the message its last words went in. */
  attach: (
    messageId: string,
    file: File,
    durationSeconds: number,
    levels: readonly number[],
    recordingId: string | null,
  ) => void;
  /** Sends the recording as a note of its own, when words are left to send with it. */
  deliver: VoiceNoteDelivery;
  /**
   * Whether the chat is replying to words sent: the recording is held
   * meanwhile, and carries on once it has replied.
   */
  isReplying?: boolean;
}

/** How much of a recording has gone out at its pauses, and in which message last. */
interface SentWords {
  chars: number;
  seconds: number;
  levels: number;
  messageId: string | null;
}

const NOTHING_SENT: SentWords = {
  chars: 0,
  seconds: 0,
  levels: 0,
  messageId: null,
};

/**
 * useVoiceNoteDraft records a voice note and captions it as it is spoken, so
 * its words count before it is sent, and sends it with its transcript. In the
 * flow of a chat, its words are sent at each pause, and it is sent as it stops.
 */
export const useVoiceNoteDraft = (
  careCircleId?: string,
  flow?: VoiceNoteFlow,
) => {
  const recorder = useAudioRecorder();
  // Held while the chat replies to what they said, so they take turns with it.
  const isHeld = recorder.state === 'recording' && !!flow?.isReplying;
  const voiceTranscript = useVoiceTranscript({
    careCircleId,
    stream: recorder.stream,
    isHeld,
  });
  // Their correction of the captions; they vouch for it, so it is read as it is.
  const [editedTranscript, setEditedTranscript] = useState<string | null>(null);
  // A note's words stay live while its last words settle, so its ticks hold until it is sent.
  const [sendingText, setSendingText] = useState('');
  const sentWords = useRef(NOTHING_SENT);
  // Read when a pause is over, not when it began.
  const latestRecorder = useRef(recorder);
  latestRecorder.current = recorder;
  const latestFlow = useRef(flow);
  latestFlow.current = flow;

  const isUnsent =
    recorder.state === 'recording' ||
    (recorder.state === 'stopped' && !!recorder.file);
  const spokenText =
    isUnsent && voiceTranscript.caption
      ? (editedTranscript ?? heardText(voiceTranscript.caption))
      : '';
  /** The words recorded and not yet sent, including those still settling. */
  const liveText = joinLines([sendingText, spokenText]);

  // Time held counts too: the live session it's transcribed in is bounded by
  // time since it started, not time recorded.
  useEffect(() => {
    if (
      recorder.state === 'recording' &&
      recorder.elapsedSeconds >= MAX_RECORDING_DURATION_SECONDS
    ) {
      recorder.stop();
    }
  }, [recorder]);

  const { pause, resume } = recorder;
  useEffect(() => {
    if (isHeld) pause();
    else resume();
  }, [isHeld, pause, resume]);

  // Their words are settled, with none coming, so they have paused: once the
  // pause has lasted, what they said since the last one is sent. Nothing goes
  // while the recording is held: a pause that lands in the hold would send
  // words into the reply. They go once it carries on, after a pause of their
  // own.
  const isFlowing = !!flow;
  const isRecording = recorder.state === 'recording';
  const settledText = voiceTranscript.caption?.text ?? '';
  const comingText = voiceTranscript.caption?.interimText ?? '';
  useEffect(() => {
    if (!isFlowing || !isRecording || isHeld || comingText) return;
    if (!settledText.slice(sentWords.current.chars).trim()) return;
    const timer = setTimeout(() => {
      const { durationSeconds, levels } = latestRecorder.current;
      const sent = sentWords.current;
      const messageId = latestFlow.current?.sendWords(
        settledText.slice(sent.chars).trim(),
        Math.max(1, durationSeconds - sent.seconds),
        levels.slice(sent.levels),
      );
      if (!messageId) return;
      sentWords.current = {
        chars: settledText.length,
        seconds: durationSeconds,
        levels: levels.length,
        messageId,
      };
    }, SPEECH_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [isFlowing, isRecording, isHeld, settledText, comingText]);

  const start = () => {
    setEditedTranscript(null);
    sentWords.current = NOTHING_SENT;
    recorder.start();
  };

  const remove = () => {
    setEditedTranscript(null);
    sentWords.current = NOTHING_SENT;
    recorder.reset();
  };

  // Cleared at once; sent once its last words settle, which is usually already.
  const send = async (deliver: VoiceNoteDelivery) => {
    const { file, durationSeconds, levels } = recorder;
    if (!file) return;
    const edited = editedTranscript;
    // Taken now: the session is ready long before the recording stops, and a
    // recording started while the words settle has an ID of its own.
    const { recordingId } = voiceTranscript;
    const sent = sentWords.current;
    setSendingText(spokenText);
    recorder.reset();
    setEditedTranscript(null);
    sentWords.current = NOTHING_SENT;
    // Only the time since the last words sent counts against how few are
    // heard: the pauses before were spent reading the replies.
    const transcript =
      edited ??
      (await voiceTranscript.takeTranscript(
        durationSeconds - sent.seconds,
        sent.chars,
      ));
    if (!sent.messageId) {
      deliver(file, durationSeconds, levels, transcript, recordingId);
    } else {
      const unsent = transcript?.slice(sent.chars).trim();
      if (unsent) {
        deliver(file, durationSeconds, levels, unsent, recordingId);
      } else {
        latestFlow.current?.attach(
          sent.messageId,
          file,
          durationSeconds,
          levels,
          recordingId,
        );
      }
    }
    setSendingText('');
  };

  // In the flow of a chat, stopping sends it: there is nothing left to review.
  const hasStopped = recorder.state === 'stopped' && !!recorder.file;
  useEffect(() => {
    const deliver = latestFlow.current?.deliver;
    if (hasStopped && deliver) send(deliver);
    // Sent once as it stops; send resets the recorder, so it doesn't run again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStopped]);

  const recorderProps: Omit<RecorderProps, 'onSend'> = {
    state: recorder.state,
    isPaused: recorder.isPaused,
    durationSeconds: recorder.durationSeconds,
    levels: recorder.levels,
    maxDurationSeconds: MAX_RECORDING_DURATION_SECONDS,
    error: recorder.error ?? voiceTranscript.error,
    file: recorder.file,
    isPlaying: recorder.isPlaying,
    liveTranscript: voiceTranscript.caption,
    isCaptionShown: voiceTranscript.isShown,
    onToggleCaption: voiceTranscript.toggleShown,
    editedTranscript,
    onEditTranscript: setEditedTranscript,
    onStart: start,
    onStop: recorder.stop,
    onTogglePlay: recorder.togglePlay,
    onDelete: remove,
  };

  return { liveText, recorderProps, send };
};
